/* eslint-disable @next/next/no-img-element */
import React, { FC } from 'react'
import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import NextImage from 'next/image'
import formatLocation from '@/utils/formatLocation'
import { BadgeCheck, BriefcaseBusiness, MapPinCheckInside, Phone } from 'lucide-react'
export interface ICompanyCard extends React.PropsWithChildren {
	logo?: string
	companyName: string
	location: string
	country: string
	offersNumber: number
	phoneNumber: string
	noBorder?: boolean
	clickable?: boolean
	companyVerified?: boolean
}

export const CompanyCard: FC<ICompanyCard> = ({
	logo,
	companyName,
	location,
	country,
	offersNumber,
	phoneNumber,
	noBorder,
	clickable,
	companyVerified,
}) => {
	const { t } = useTranslation('companies')
	const { t: tCommon } = useTranslation('common')
	const { t: tCompany } = useTranslation('company')
	return (
		<div
			className={classNames(
				'bg-white border border-solid border-brand-gray-200 group shadow-md rounded-xl h-full overflow-hidden relative duration-300 hover:shadow-2xl hover:border-brand-primary-md',
				{
					'border-none shadow-none': noBorder,
					'cursor-pointer hover:shadow-2xl': clickable,
				},
			)}
		>
			<div className="absolute top-0 left-0 w-full h-[6px] bg-brand-primary-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

			<div className="p-4 space-y-4">
				<div className="flex items-center gap-3 relative">
					<div className="w-[60px] h-[60px] relative flex-shrink-0 rounded-md overflow-hidden">
						<NextImage
							width={60}
							height={60}
							src={logo || 'https://i.ibb.co/Rc6D9L1/constraction-znak-cmyk.png'}
							alt={'company-logo'}
							className="w-full h-full object-cover object-center"
							style={{
								objectFit: 'contain',
								objectPosition: 'center',
							}}
						/>
					</div>
					<div className="flex-1 pr-8">
						<p className="text-xl font-semibold text-brand-gray-400 line-clamp-2 break-words">{companyName}</p>
					</div>

					{companyVerified && (
						<div className="absolute top-0 right-0 group/tooltip z-10">
							<div className="bg-green-100 rounded-full p-1 cursor-help">
								<BadgeCheck className="w-5 h-5 text-green-600" />
							</div>
							<div className="absolute invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-300 top-full -right-1/3 mt-2 w-64 bg-gray-900 text-white text-xs rounded p-2 shadow-lg z-50">
								{tCompany('verified-company-tooltip')}
								<div className="absolute bottom-full right-4 -mb-1 border-x-8 border-x-transparent border-b-8 border-b-gray-900"></div>
							</div>
						</div>
					)}
				</div>

				<div className="bg-brand-gray-100/60 p-4 rounded-lg">
					<div className="flex flex-col gap-2 text-sm text-black text-opacity-70 font-medium">
						{(location || country) && (
							<>
								<p className="flex items-center gap-2 capitalize">
									<MapPinCheckInside className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
									{formatLocation(tCommon(`countries.${country}`), location)}
								</p>
								<div className="w-full h-[1px] bg-gray-300"></div>
							</>
						)}
						{phoneNumber && (
							<>
								<p className="flex items-center gap-2">
									<Phone className="w-5 h-5 text-brand-primary-md" strokeWidth={1.5} />
									{phoneNumber}
								</p>
								<div className="w-full h-[1px] bg-gray-300"></div>
							</>
						)}
						<p className="flex items-center gap-2">
							<BriefcaseBusiness className="w-5 h-5 text-brand-primary-md" strokeWidth={1.5} />
							{t('job-offers', { count: offersNumber || 0 })}
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}

CompanyCard.displayName = 'CompanyCard'
